import { parsePath } from "history";

import remixOwnedRoutePatterns from "./remixOwnedPaths.generated.json";

/**
 * Returns true if `to` is served by Remix.
 *
 * Accepts the same shapes that react-router-dom's `<Link to>` does — a
 * string path or a `Partial<Path>` object (`{ pathname?, search?, hash? }`).
 * Query string and hash are ignored; only the pathname is matched.
 *
 * Source of truth is `./remixOwnedPaths.generated.json`, derived from the
 * Remix route tree by `yarn remix:routes` (generator lives at
 * tools/frontend/remix-owned-paths.ts). Adding a new route under
 * `app/javascript/remix/routes/` and running that command makes the
 * cross-app <Link>/<NavLink>/useNavigate shims aware of it automatically.
 *
 * Matching is exact and segment-aware, mirroring how the Remix router
 * resolves the route tree. A `:param` matches exactly one path segment; a
 * trailing `*` (splat) is the only way a pattern owns descendants. So
 * `/o/orders/:orderId` matches `/o/orders/123` but NOT the bare `/o/orders`
 * parent nor a deeper `/o/orders/123/edit` (which would be its own route
 * entry if Remix owned it). Note `:orderId` still matches any single value,
 * including a concrete Rails sibling like `/o/orders/report` — the generated
 * list knows only Remix routes, not Rails ones, so keeping a concrete sibling
 * on Rails while the dynamic route is migrated is a migration-ordering
 * concern, not something this matcher can resolve.
 *
 * A `to` with no concrete pathname (empty string, empty object,
 * `{ search: "..." }` only) means "current location" in react-router-dom —
 * we treat those as Remix-owned so the click stays in whichever router is
 * currently mounted instead of triggering a document navigation.
 */
const isRemixOwnedPath = (to: unknown): boolean => {
  const pathname = extractPathname(to);

  if (pathname === undefined) return false;
  if (pathname === "") return true;

  return OWNED_PATH_MATCHERS.some(matcher => matcher.test(pathname));
};

const extractPathname = (to: unknown): string | undefined => {
  if (typeof to === "string") {
    // parsePath splits off search/hash without treating "//foo" as a
    // protocol-relative URL (which `new URL(to, base)` would).
    return stripTrailingSlash(parsePath(to).pathname ?? "");
  }

  // Only accept plain objects (Partial<Path> shape). Arrays, Date, class
  // instances, etc. all share `typeof === "object"` so a plain typeof check
  // is too permissive.
  if (to === null || typeof to !== "object") return undefined;
  if (Object.getPrototypeOf(to) !== Object.prototype) return undefined;

  const { pathname } = to as { pathname?: unknown };

  if (pathname === undefined) return "";
  if (typeof pathname !== "string") return undefined;

  return stripTrailingSlash(pathname);
};

// The generated patterns are stored without trailing slashes, so normalize
// the incoming pathname too — "/emerson/" should match "/emerson". The
// `entry.server.tsx` redirect strips trailing slashes for real document
// requests, but this helper also runs on user-/externally-sourced paths
// (CMS links, raw `window.location.pathname`), so don't rely on that alone.
// Never strip the bare root "/" — that would turn it into "" ("current
// location"), which is a different meaning.
const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

const escapeRegExp = (literal: string): string => literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Compiles a generated route pattern into a matcher RegExp.
 *
 * Matching is exact, mirroring how the Remix router resolves the route tree:
 * the manifest enumerates every owned route, so a pattern owns only its own
 * path, not arbitrary descendants. The sole exception is a trailing splat,
 * which is Remix's explicit "I own everything below me".
 *
 * - `:param` → `[^/]+` (exactly one path segment)
 * - a trailing `*` (remix splat) → owns the path and everything below it
 * - literal segments are regex-escaped
 */
const pathPatternToRegExp = (pattern: string): RegExp => {
  if (pattern.endsWith("/*")) {
    // Splat: "/foo/*" owns "/foo" and every descendant. Drop the marker and
    // anchor with an optional descendant tail.
    const base = compileSegments(pattern.slice(0, -2));

    return new RegExp(`^${base}(?:/.*)?$`);
  }

  return new RegExp(`^${compileSegments(pattern)}$`);
};

const compileSegments = (pattern: string): string =>
  pattern
    .split("/")
    .map(segment => {
      if (segment === "") return "";
      if (segment.startsWith(":")) return "[^/]+";

      return escapeRegExp(segment);
    })
    .join("/");

const OWNED_PATH_MATCHERS = remixOwnedRoutePatterns.map(pathPatternToRegExp);

export { isRemixOwnedPath, pathPatternToRegExp };
